import cn from 'classnames'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import { white100 } from 'ext-ui/style/colors'
import { flyout_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import React, { ReactElement, useState } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import style from './BaseFlyout.style'

export type BaseFlyoutProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `focus-background` variant when the content behind the flyout
   * shouldn't be fully obscured (e.g. for a filters flyout).
   *
   * Use the `default` variant in all other cases.
   */
  variant?: 'default' | 'focus-background'
  /** Function to run on close button click */
  onClose: () => void
  /**
   * Shows the primary content close button if `true` (default). Set `false` for
   * consumers whose content renders its own close affordance (e.g. the legacy
   * `RightFlyout`, whose content already provides a header + close button).
   */
  showCloseButton?: boolean
  /** `id` applied to the primary content box (`.BaseFlyout-Content`). */
  contentId?: string
  /** `id` applied to the primary content body (`.BaseFlyout-Body`). */
  bodyId?: string
  title?: string
  subtitle?: string
  secondaryFlyoutContent?: {
    panelTab: 'new' // expecting other variants in the future
    title?: string
    subtitle?: string
    content: React.ReactNode
  }
}

function BaseFlyout(
  props: BaseFlyoutProps & { 'aria-label': string },
): ReactElement {
  const {
    className = '',
    isOpen,
    variant = 'default',
    children,
    onClose,
    showCloseButton = true,
    contentId,
    bodyId,
    title,
    subtitle,
    secondaryFlyoutContent,
    'aria-label': ariaLabel,
    // ...rest
  } = props

  const [showSecondaryContent, setShowSecondaryContent] = useState(false)

  return (
    <Modal
      className={cn('BaseFlyout-Container', variant)}
      overlayClassName={{
        base: 'BaseFlyout-Overlay',
        afterOpen: 'BaseFlyout-Overlay-open',
        beforeClose: 'BaseFlyout-Overlay-closed',
      }}
      portalClassName={cn('BaseFlyout', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={parseInt(flyout_transitionSpeed)}
      bodyOpenClassName="modal-open"
      testId="flyout"
      contentLabel={ariaLabel}
    >
      {secondaryFlyoutContent && (
        <>
          <button
            className={cn(
              'BaseFlyout-PanelTab',
              secondaryFlyoutContent.panelTab,
              {
                showSecondaryContent,
              },
            )}
            data-testid={'panelTab'}
            onClick={() => setShowSecondaryContent(true)}
          >
            <AiStarsIcon fill={white100} />
          </button>
          <div
            data-testid={'secondaryFlyout'}
            className={cn('BaseFlyout-Content secondaryFlyout', {
              showSecondaryContent,
            })}
          >
            <div className="BaseFlyout-CloseBar">
              <CloseButton
                data-testid={'closeSecondaryFlyout'}
                className="BaseFlyout-CloseBar-CloseButton"
                variant="dark-on-light"
                onClick={() => setShowSecondaryContent(false)}
              />
            </div>
            {(secondaryFlyoutContent.title ||
              secondaryFlyoutContent.subtitle) && (
              <header className="BaseFlyout-Header">
                {secondaryFlyoutContent.title && (
                  <h2>{secondaryFlyoutContent.title}</h2>
                )}
                {secondaryFlyoutContent.subtitle && (
                  <p>{secondaryFlyoutContent.subtitle}</p>
                )}
              </header>
            )}
            <div className="BaseFlyout-Body">
              {secondaryFlyoutContent.content}
            </div>
          </div>
        </>
      )}
      <div
        className={'BaseFlyout-Content'}
        data-testid={'primaryFlyout'}
        id={contentId}
      >
        {showCloseButton && (
          <div className="BaseFlyout-CloseBar">
            <CloseButton
              data-testid={'closePrimaryFlyout'}
              className="BaseFlyout-CloseBar-CloseButton"
              variant="dark-on-light"
              onClick={onClose}
            />
          </div>
        )}
        {(title || subtitle) && (
          <header className="BaseFlyout-Header">
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </header>
        )}
        <div className="BaseFlyout-Body" id={bodyId}>
          {children}
        </div>
      </div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Flyout>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseFlyout)`
  ${style}
`
